import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { AdCreditBalance, useAdCredits, toastCreditError } from "@/components/AdCreditBalance";
import { fmtMoney } from "@/lib/payments";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useIsMobile } from "@/lib/useIsMobile";
import { FileUpload, resolveMediaUrl, onMediaImgError } from "@/components/FileUpload";
import VideoPlayer from "@/components/VideoPlayer";
import { AdPreview, ReelsAdPreview } from "@/components/AdPreview";
import ImageCropUpload, { AD_ASPECTS } from "@/components/ImageCropUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody } from "@/components/ui/dialog";
import { CTA_TYPES, CTA_OPTIONS, CTA_LABELS } from "@/lib/adCta";
import {
  Megaphone, Eye, MousePointerClick, TrendingUp, Plus, X, Users, Mail, Phone, MessageSquare, Target,
  Download, BarChart3, Trash2, ImagePlus, Video, RefreshCw, AlertTriangle, ChevronLeft, ChevronRight, CalendarClock,
} from "lucide-react";
import { ResponsiveContainer, ComposedChart, BarChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";

const downloadLeadsCsv = (ad, leads) => {
  const header = ["Nom", "Email", "Téléphone", "Message", "Date"];
  const rows = (leads || []).map((l) => [l.name || "", l.email || "", l.phone || "", (l.message || "").replace(/\n/g, " "), new Date(l.created_at).toLocaleString("fr-FR")]);
  const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
  const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\r\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `prospects-${(ad?.title || "pub").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const EMPTY = {
  title: "", body: "", media_url: "", media_type: "image", poster_url: "", target_url: "", budget: 20, days: 7, starts_at: "",
  cta_type: "lead_form", cta_label: "get_quote", cta_value: "", cta_headline: "", cta_description: "",
  lead_fields: ["name", "phone"], custom_fields: [],
  t_genders: [], t_age_min: "", t_age_max: "", t_age_ranges: [], t_countries: "", t_cities: "", t_denominations: [], t_interests: [], t_churches: [],
  offline: false, advertiser_name: "", as_church_id: "", manual_price: "", billing_mode: "",
};
const AD_BILLING_MODES = ["free", "bank_transfer", "cash", "paypal", "other"];
// Assistant mobile : pastilles budget / durée, tranches d'âge cliquables, brouillon local.
// Repli si les packs de recharge (Admin › Finances) ne sont pas encore chargés.
const BUDGET_PRESETS = [5, 20, 50];
const DAYS_PRESETS = [3, 7, 14, 30];
const AGE_RANGES = [
  { label: "13-17", min: 13, max: 17 }, { label: "18-24", min: 18, max: 24 }, { label: "25-34", min: 25, max: 34 },
  { label: "35-44", min: 35, max: 44 }, { label: "45-54", min: 45, max: 54 }, { label: "55-64", min: 55, max: 64 }, { label: "65+", min: 65, max: 0 },
];
const STEP_KEYS = ["Visuel & message", "Bouton & audience", "Budget & durée"];
const DRAFT_KEY = "yalola.ads.draft";
const readDraft = () => { try { const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); return d && typeof d === "object" ? d : null; } catch { return null; } };
const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ } };
// datetime-local minimum = maintenant + 1 h (heure locale, sans secondes).
const localMinStart = () => { const d = new Date(Date.now() + 3600000); d.setSeconds(0, 0); const pad = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; };

const StatCard = ({ label, value, color }) => (
  <div className="yalola-card p-4">
    <div className="text-[11px] uppercase font-bold tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{label}</div>
    <div className="text-2xl font-extrabold mt-1 font-display" style={{ color: color || "var(--yalola-text)" }}>{value}</div>
  </div>
);

const LeadsDialog = ({ ad, open, onOpenChange }) => {
  const { t } = useApp();
  const [leads, setLeads] = useState(null);
  useEffect(() => {
    if (open && ad) {
      setLeads(null);
      api.get(`/ads/${ad.id}/leads`).then(({ data }) => setLeads(data.leads || [])).catch(() => setLeads([]));
    }
  }, [open, ad]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ad-leads-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Users size={18} /> {t("ads.leads_title", "Prospects")} — {ad?.title}</DialogTitle>
          <DialogDescription>{t("ads.leads_desc", "Coordonnées laissées par les membres via le formulaire de votre publicité.")}</DialogDescription>
        </DialogHeader>
        {leads && leads.length > 0 && (
          <div className="flex justify-end">
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadLeadsCsv(ad, leads)} data-testid="ad-leads-csv">
              <Download size={14} className="mr-1" /> {t("ads.leads_csv", "Télécharger en CSV")} ({leads.length})
            </Button>
          </div>
        )}
        <div className="space-y-2">
          {leads === null && <div className="py-8 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.leads_loading", "Chargement…")}</div>}
          {leads && leads.length === 0 && <div className="py-8 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="ad-leads-empty">{t("ads.leads_empty", "Aucun prospect pour l'instant.")}</div>}
          {leads && leads.map((l) => (
            <div key={l.id} className="rounded-xl border p-3" style={{ borderColor: "var(--yalola-border)" }} data-testid={`ad-lead-row-${l.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-sm">{l.name || t("ads.lead_no_name", "Sans nom")}</div>
                <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{new Date(l.created_at).toLocaleString("fr-FR")}</div>
              </div>
              <div className="flex flex-col gap-1 mt-1.5 text-sm">
                {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-2 hover:underline"><Mail size={13} /> {l.email}</a>}
                {l.phone && <a href={`tel:${l.phone}`} className="flex items-center gap-2 hover:underline"><Phone size={13} /> {l.phone}</a>}
                {l.message && <div className="flex items-start gap-2" style={{ color: "var(--yalola-text-muted)" }}><MessageSquare size={13} className="mt-0.5" /> {l.message}</div>}
                {(l.answers || []).filter((a) => a.value).map((a) => (
                  <div key={a.id} className="flex items-start gap-2" data-testid={`ad-lead-answer-${l.id}-${a.id}`}>
                    <span className="font-medium">{a.label} :</span>
                    <span style={{ color: "var(--yalola-text-muted)" }}>{a.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const fmtWatch = (s) => {
  if (!s || s <= 0) return "0 s";
  if (s < 60) return `${Math.round(s)} s`;
  return `${Math.floor(s / 60)} min ${String(Math.round(s % 60)).padStart(2, "0")}`;
};

const QuartileFunnel = ({ totals, t }) => {
  const starts = totals.video_starts || 0;
  const rows = [
    { k: "q25", label: t("ads.q25", "25 % vus"), v: totals.video_q25 || 0, c: "#2E7CF6" },
    { k: "q50", label: t("ads.q50", "50 % vus"), v: totals.video_q50 || 0, c: "#2D9E68" },
    { k: "q75", label: t("ads.q75", "75 % vus"), v: totals.video_q75 || 0, c: "#E0A63C" },
    { k: "q100", label: t("ads.q100", "Vidéo entière"), v: totals.video_q100 || 0, c: "#D97757" },
  ];
  return (
    <div className="rounded-xl p-3 mb-3" style={{ background: "var(--yalola-surface-alt)" }} data-testid="ad-stats-video-funnel">
      <div className="text-[11px] font-semibold uppercase mb-2" style={{ color: "var(--yalola-text-muted)" }}>
        {t("ads.watch_milestones", "Paliers de visionnage")}{starts ? ` — ${t("ads.on_starts", "sur")} ${starts} ${t("ads.starts_word", "démarrage(s)")}` : ""}
      </div>
      <div className="space-y-1.5">
        {rows.map((r) => {
          const pct = starts ? Math.min(100, Math.round((r.v / starts) * 100)) : 0;
          return (
            <div key={r.k} className="flex items-center gap-2" data-testid={`ad-funnel-${r.k}`}>
              <span className="text-[11px] w-24 shrink-0" style={{ color: "var(--yalola-text-muted)" }}>{r.label}</span>
              <div className="flex-1 h-4 rounded-full overflow-hidden" style={{ background: "var(--yalola-border)" }}>
                <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, background: r.c }} />
              </div>
              <span className="text-[11px] font-bold w-20 text-right">{r.v} · {pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StatsDialog = ({ ad, open, onOpenChange }) => {
  const { t } = useApp();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (open && ad) {
      setData(null);
      api.get(`/ads/${ad.id}/stats`, { params: { days: 14 } }).then(({ data }) => setData(data)).catch(() => setData({ series: [], totals: {} }));
    }
  }, [open, ad]);
  const series = (data?.series || []).map((r) => ({
    ...r,
    jour: r.date.slice(5),
    ctr: r.impressions ? Number(((r.clicks / r.impressions) * 100).toFixed(1)) : 0,
  }));
  const lblImpr = t("ads.stat_impressions", "Impressions");
  const lblClicks = t("ads.stat_clicks", "Clics");
  const lblLeads = t("ads.stat_leads", "Prospects");
  const lblMessages = t("ads.stat_messages", "Conversations");
  const totalCtr = data?.totals?.impressions ? ((data.totals.clicks / data.totals.impressions) * 100).toFixed(1) : "0.0";
  const tot = data?.totals || {};
  const isVideoAd = data?.media_type === "video" || (tot.video_starts || 0) > 0;
  const avgWatch = tot.video_starts ? (tot.video_watch_seconds || 0) / tot.video_starts : 0;
  const completion = tot.video_starts ? Math.min(100, Math.round(((tot.video_q100 || 0) / tot.video_starts) * 100)) : 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="ad-stats-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><BarChart3 size={18} /> {t("ads.stats_title", "Statistiques")} — {ad?.title}</DialogTitle>
          <DialogDescription>{t("ads.stats_desc_ctr", "Impressions, clics et taux de clic (CTR) sur les 14 derniers jours.")}</DialogDescription>
        </DialogHeader>
        {!data && <div className="py-10 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.leads_loading", "Chargement…")}</div>}
        {data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
              <div className="rounded-xl p-3 text-center" style={{ background: "var(--yalola-surface-alt)" }}><div className="text-xl font-extrabold">{data.totals?.impressions || 0}</div><div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{lblImpr}</div></div>
              <div className="rounded-xl p-3 text-center" style={{ background: "var(--yalola-surface-alt)" }}><div className="text-xl font-extrabold">{data.totals?.clicks || 0}</div><div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{lblClicks}</div></div>
              <div className="rounded-xl p-3 text-center" style={{ background: "var(--yalola-surface-alt)" }} data-testid="ad-stats-ctr"><div className="text-xl font-extrabold" style={{ color: "var(--yalola-primary)" }}>{totalCtr}%</div><div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.stat_ctr", "Taux de clic")}</div></div>
              <div className="rounded-xl p-3 text-center" style={{ background: "var(--yalola-surface-alt)" }}><div className="text-xl font-extrabold">{data.totals?.leads || 0}</div><div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{lblLeads}</div></div>
              <div className="rounded-xl p-3 text-center" style={{ background: "var(--yalola-surface-alt)" }} title={t("ads.stat_messages_hint", "Membres ayant écrit à l'annonceur depuis le bouton de la publicité (1 par personne).")} data-testid="ad-stats-messages"><div className="text-xl font-extrabold">{data.totals?.messages || 0}</div><div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{lblMessages}</div></div>
            </div>
            {isVideoAd && (
              <>
                <div className="grid grid-cols-3 gap-2 mb-3" data-testid="ad-stats-video-cards">
                  <div className="rounded-xl p-3 text-center" style={{ background: "var(--yalola-surface-alt)" }} data-testid="ad-stats-video-starts"><div className="text-xl font-extrabold">{tot.video_starts || 0}</div><div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.stat_video_starts", "Démarrages vidéo")}</div></div>
                  <div className="rounded-xl p-3 text-center" style={{ background: "var(--yalola-surface-alt)" }} data-testid="ad-stats-avg-watch"><div className="text-xl font-extrabold" style={{ color: "var(--yalola-primary)" }}>{fmtWatch(avgWatch)}</div><div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.stat_avg_watch", "Durée moy. regardée")}</div></div>
                  <div className="rounded-xl p-3 text-center" style={{ background: "var(--yalola-surface-alt)" }} data-testid="ad-stats-completion"><div className="text-xl font-extrabold" style={{ color: "#2D9E68" }}>{completion}%</div><div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.stat_completion", "Vues complètes")}</div></div>
                </div>
                <QuartileFunnel totals={tot} t={t} />
              </>
            )}
            <div className="h-64 w-full" data-testid="ad-stats-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={series} margin={{ top: 5, right: 6, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--yalola-border)" />
                  <XAxis dataKey="jour" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="count" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis yAxisId="ctr" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} width={38} />
                  <Tooltip formatter={(val, name) => (name === "CTR %" ? [`${val}%`, name] : [val, name])} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="count" dataKey="impressions" name={lblImpr} fill="#2E7CF6" radius={[3, 3, 0, 0]} />
                  <Bar yAxisId="count" dataKey="clicks" name={lblClicks} fill="#D97757" radius={[3, 3, 0, 0]} />
                  {isVideoAd && <Bar yAxisId="count" dataKey="video_starts" name={t("ads.stat_video_starts", "Démarrages vidéo")} fill="#8B5CF6" radius={[3, 3, 0, 0]} />}
                  <Line yAxisId="ctr" type="monotone" dataKey="ctr" name="CTR %" stroke="#2D9E68" strokeWidth={2} dot={{ r: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};


// Shared with the Admin console (Pubs panel) so admins see the same analytics.
export const AdStatsDialog = StatsDialog;


export const AdsManager = () => {
  const { config, lang, user, t } = useApp();
  const isMobile = useIsMobile();
  const curSym = (config?.settings?.default_currency || "USD") === "EUR" ? "€" : (!config?.settings?.default_currency || config.settings.default_currency === "USD") ? "$" : config.settings.default_currency;
  const denominations = config?.denominations || [];
  const interests = config?.interests || [];
  const [ads, setAds] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [previewMode, setPreviewMode] = useState("feed");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [minStart] = useState(localMinStart);
  const [showTargeting, setShowTargeting] = useState(false);
  const [leadsAd, setLeadsAd] = useState(null);
  const [statsAd, setStatsAd] = useState(null);
  const [reactivateAd, setReactivateAd] = useState(null);
  const [reactDays, setReactDays] = useState(7);
  const isStaff = ["super_admin", "manager"].includes(user?.role);
  const isSuper = user?.role === "super_admin";
  const payFree = isSuper || (isStaff && form.offline);
  const { data: credits, balance: creditBalance, currency: creditCur, reload: reloadCredits } = useAdCredits(!!user && !isSuper);
  // Pastilles budget = packs de recharge configurés (in-app iOS/Android, Stripe web).
  const budgetPresets = credits?.packs?.length ? credits.packs : (config?.payments?.ad_credit_packs?.length ? config.payments.ad_credit_packs : BUDGET_PRESETS);
  // Bandeau « Solde bas » : campagnes payées en crédits, actives, qui se terminent sous 3 jours et dont
  // une journée au tarif actuel (budget / jours) dépasse le solde — même règle que la notification J-3.
  const lowBalanceAds = ads.flatMap((a) => {
    if (a.paid_with !== "ad_credit" || !a.active || a.payment_status === "pending" || !a.expires_at) return [];
    const left = new Date(a.expires_at).getTime() - Date.now();
    if (left < 0 || left > 3 * 86400000) return [];
    const daily = (Number(a.budget) || 0) / Math.max(1, Number(a.days) || 1);
    return daily > 0 && creditBalance < daily ? [{ ...a, daily }] : [];
  });
  const whenLabel = (expiresAt) => {
    const d = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 86400000));
    return d === 0 ? t("notif.when_today", "aujourd'hui") : d === 1 ? t("notif.when_tomorrow", "demain") : t("notif.when_days", "dans {n} jours").replace("{n}", String(d));
  };
  const [myChurches, setMyChurches] = useState([]);
  useEffect(() => {
    if (isStaff) api.get("/churches/mine").then(({ data }) => setMyChurches(data.churches || [])).catch(() => {});
  }, [isStaff]);

  // Church targeting picker (priority boost for members of the chosen churches).
  const [churchQuery, setChurchQuery] = useState("");
  const [churchResults, setChurchResults] = useState([]);
  useEffect(() => {
    const q = churchQuery.trim();
    if (q.length < 2) { setChurchResults([]); return; }
    const id = setTimeout(() => {
      api.get(`/churches?search=${encodeURIComponent(q)}&verified_only=false`)
        .then(({ data }) => setChurchResults((data.churches || []).slice(0, 8)))
        .catch(() => setChurchResults([]));
    }, 300);
    return () => clearTimeout(id);
  }, [churchQuery]);
  const addChurch = (c) => {
    setForm((f) => f.t_churches.some((x) => x.id === c.id) ? f : { ...f, t_churches: [...f.t_churches, { id: c.id, name: c.name }] });
    setChurchQuery(""); setChurchResults([]);
  };
  const removeChurch = (cid) => setForm((f) => ({ ...f, t_churches: f.t_churches.filter((x) => x.id !== cid) }));

  // Tranches d'âge cliquables → âge min / max (bornes des tranches choisies ; « 65+ » = sans maximum).
  const setAgeRanges = (labels) => {
    const sel = AGE_RANGES.filter((r) => labels.includes(r.label));
    if (!sel.length) { setForm((f) => ({ ...f, t_age_ranges: [], t_age_min: "", t_age_max: "" })); return; }
    const min = Math.min(...sel.map((r) => r.min));
    const max = sel.some((r) => r.max === 0) ? 0 : Math.max(...sel.map((r) => r.max));
    setForm((f) => ({ ...f, t_age_ranges: labels, t_age_min: String(min), t_age_max: max ? String(max) : "" }));
  };

  // Brouillon local : restauré à l'ouverture du formulaire, sauvegardé à chaque modification (téléphone
  // interrompu, appel, retour arrière…), effacé au lancement ou à l'annulation.
  useEffect(() => {
    if (!showForm) return;
    const d = readDraft();
    if (d && (d.title || d.media_url || d.body)) {
      setForm({ ...EMPTY, ...d });
      toast.info(t("ads.draft_restored", "Brouillon restauré"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);
  useEffect(() => {
    if (!showForm) return;
    const id = setTimeout(() => { try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch { /* noop */ } }, 400);
    return () => clearTimeout(id);
  }, [form, showForm]);
  const cancelForm = () => { clearDraft(); setForm(EMPTY); setStep(1); setShowForm(false); };
  // Sur téléphone, l'audience (étape 2) est ouverte d'emblée : pas de « + » à découvrir.
  useEffect(() => { if (isMobile) setShowTargeting(true); }, [isMobile]);

  // Assistant mobile : l'étape 1 exige un titre + un visuel ; les autres étapes sont libres.
  const stepValid = (n) => (n === 1 ? !!(form.title.trim() && form.media_url) : true);
  // Sur téléphone, chaque changement d'étape remonte au TITRE du formulaire (sous la barre du haut), pas en haut de la page.
  const formRef = useRef(null);
  const scrollToForm = () => {
    const el = formRef.current;
    if (el) el.scrollIntoView({ block: "start", behavior: "smooth" }); else window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goNext = () => {
    if (step === 1 && !stepValid(1)) { toast.info(t("ads.validation_step1", "Ajoute un titre et un visuel pour continuer")); return; }
    setStep((s) => Math.min(3, s + 1));
    scrollToForm();
  };
  const goStep = (n) => { setStep(n); scrollToForm(); };
  // Téléphone : le formulaire DEVIENT l'écran (crédits, stats et liste masqués) → on repart du haut à l'ouverture.
  const formScreen = showForm && isMobile;
  useEffect(() => { if (formScreen) window.scrollTo({ top: 0 }); }, [formScreen]);

  const buildTargeting = () => ({
    genders: form.t_genders,
    age_min: Number(form.t_age_min) || 0,
    age_max: Number(form.t_age_max) || 0,
    countries: form.t_countries.split(",").map((s) => s.trim()).filter(Boolean),
    cities: form.t_cities.split(",").map((s) => s.trim()).filter(Boolean),
    denominations: form.t_denominations,
    interests: form.t_interests,
    churches: form.t_churches.map((c) => c.name),
    church_ids: form.t_churches.map((c) => c.id),
  });

  // Live audience estimate + recommended budget/duration for the current targeting.
  const [estimate, setEstimate] = useState(null);
  useEffect(() => {
    if (!showForm) return;
    const tg = buildTargeting();
    const id = setTimeout(() => {
      api.post("/ads/estimate", { targeting: tg, days: Number(form.days) || 7, budget: Number(form.budget) || 0 })
        .then(({ data }) => setEstimate(data))
        .catch(() => setEstimate(null));
    }, 500);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, form.t_genders, form.t_age_min, form.t_age_max, form.t_countries, form.t_cities, form.t_denominations, form.t_interests, form.t_churches, form.days, form.budget]);

  const LEAD_FIELD_OPTS = [
    { key: "name", label: t("auth.name", "Nom") },
    { key: "email", label: t("auth.email", "E-mail") },
    { key: "phone", label: t("ads.cta_phone_label", "Téléphone") },
    { key: "message", label: t("comment.placeholder", "Message") },
  ];

  const load = () => api.get("/ads/mine").then(({ data }) => setAds(data.ads || [])).catch(() => {});
  const navigate = useNavigate();
  useEffect(() => { load(); }, []);

  const toggleArr = (key, val) => setForm((f) => ({
    ...f,
    [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
  }));

  const onMedia = (url, results) => {
    const r = Array.isArray(results) ? results[0] : results;
    setForm((f) => ({ ...f, media_url: url, media_type: r?.media_type === "video" ? "video" : "image", poster_url: r?.poster_url || "" }));
  };

  const toggleLeadField = (k) => setForm((f) => ({
    ...f,
    lead_fields: f.lead_fields.includes(k) ? f.lead_fields.filter((x) => x !== k) : [...f.lead_fields, k],
  }));

  const addCustomField = () => setForm((f) => (f.custom_fields.length >= 5 ? f : {
    ...f,
    custom_fields: [...f.custom_fields, { id: `q${Date.now()}`, label: "", type: "text", options: "", required: false }],
  }));
  const updateCustomField = (i, patch) => setForm((f) => ({ ...f, custom_fields: f.custom_fields.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));
  const removeCustomField = (i) => setForm((f) => ({ ...f, custom_fields: f.custom_fields.filter((_, j) => j !== i) }));

  const create = async () => {
    if (!form.title.trim()) { toast.info(t("ads.toast_need_title", "Ajoute un titre à ta campagne")); return; }
    if (!form.media_url) { toast.info(t("ads.toast_need_media", "Ajoute une image ou une vidéo")); return; }
    const offline = isStaff && form.offline;
    if (!offline && (Number(form.budget) || 0) < 1) { toast.info(t("ads.toast_min_budget", "Budget minimum : 1 $").replace(/\$/g, curSym)); return; }
    if (offline && !form.billing_mode) { toast.info(t("ads.billing_required", "Choisis le mode de facturation.")); return; }
    if (offline && form.billing_mode !== "free" && !(Number(form.manual_price) > 0)) { toast.info(t("ads.billing_amount_required", "Indique le montant facturé (supérieur à 0).")); return; }
    setCreating(true);
    try {
      const cta = form.cta_type === "none" ? null : {
        label: form.cta_label,
        type: form.cta_type,
        value: form.cta_value,
        headline: form.cta_headline,
        description: form.cta_description,
        lead_fields: form.lead_fields,
        custom_fields: form.cta_type === "lead_form"
          ? form.custom_fields.filter((c) => c.label.trim()).slice(0, 5).map((c) => ({
              id: c.id,
              label: c.label.trim(),
              type: c.type,
              options: c.type === "select" ? String(c.options || "").split(",").map((s) => s.trim()).filter(Boolean) : [],
              required: !!c.required,
            }))
          : [],
      };
      const targeting = buildTargeting();
      const startsIso = form.starts_at ? new Date(form.starts_at).toISOString() : null;
      await api.post("/ads", {
        title: form.title, body: form.body, media_url: form.media_url, media_type: form.media_type,
        poster_url: form.poster_url || null,
        target_url: form.target_url, budget: Number(form.budget) || 0, days: Number(form.days) || 7,
        starts_at: startsIso,
        cta, targeting,
        ...(offline ? {
          advertiser_name: form.as_church_id ? "" : (form.advertiser_name || "").trim(),
          as_church_id: form.as_church_id || null,
          billing_mode: form.billing_mode,
          manual_price: form.billing_mode === "free" ? 0 : Number(form.manual_price),
        } : {}),
      });
      toast.success(offline ? t("ads.offline_created", "Publicité hors-ligne créée ✅") : payFree ? t("ads.toast_created", "Campagne publicitaire créée 🎉") : t("ads.toast_created_credits", "Campagne créée 🎉 Le budget a été débité de tes crédits publicitaires."));
      clearDraft(); setForm(EMPTY); setStep(1); setShowForm(false); load(); reloadCredits();
    } catch (e) { toastCreditError(e, t, navigate, t("ads.toast_error", "Erreur")); }
    finally { setCreating(false); }
  };

  const payNow = async (ad) => {
    try {
      await api.post(`/ads/${ad.id}/checkout`, {});
      toast.success(t("ads.toast_paid_credits", "Campagne payée avec tes crédits ✓"));
      load(); reloadCredits();
    } catch (e) { toastCreditError(e, t, navigate, t("ads.toast_error", "Erreur")); }
  };

  const toggle = async (ad) => {
    try { await api.patch(`/ads/${ad.id}`, { active: !ad.active }); load(); }
    catch { toast.error(t("ads.toast_error", "Erreur")); }
  };
  const remove = async (ad) => {
    try { await api.delete(`/ads/${ad.id}`); toast.success(t("ads.toast_deleted", "Campagne supprimée")); load(); }
    catch { toast.error(t("ads.toast_error", "Erreur")); }
  };
  const reactivate = async () => {
    if (!reactivateAd) return;
    try {
      const { data } = await api.post(`/ads/${reactivateAd.id}/reactivate/checkout`, { days: reactDays });
      if (data.paid) {
        setReactivateAd(null);
        toast.success(t("ads.toast_reactivated_credits", "Campagne réactivée ✓ Le montant a été débité de tes crédits."));
        load(); reloadCredits();
      } else { toast.error(t("ads.toast_error", "Erreur")); }
    } catch (e) { toastCreditError(e, t, navigate, t("ads.toast_error", "Erreur")); }
  };

  const now = new Date();
  const totalImpr = ads.reduce((a, b) => a + (b.impressions || 0), 0);
  const totalClicks = ads.reduce((a, b) => a + (b.clicks || 0), 0);
  const totalLeads = ads.reduce((a, b) => a + (b.leads || 0), 0);
  const ctr = totalImpr ? ((totalClicks / totalImpr) * 100).toFixed(1) : "0.0";

  return (
    <AppShell showSidebars={false}>
      <div className="max-w-[880px] mx-auto space-y-4" data-testid="ads-manager-page" data-form-screen={formScreen ? "1" : undefined}>
        {formScreen ? (
          <div className="flex items-center gap-2 px-1" data-testid="ads-form-header">
            <div className="h-9 w-9 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><Megaphone size={18} /></div>
            <div className="flex-1 min-w-0 font-bold font-display text-base truncate">{t("ads.manager_title", "Gestionnaire de publicité")}</div>
            <button type="button" onClick={cancelForm} className="h-9 w-9 grid place-items-center rounded-full shrink-0" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text)" }} aria-label={t("ads.cancel", "Annuler")} data-testid="ad-form-close"><X size={18} /></button>
          </div>
        ) : (
        <div className="yalola-card p-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><Megaphone size={20} /></div>
              <h1 className="text-2xl md:text-3xl font-bold font-display">{t("ads.manager_title", "Gestionnaire de publicité")}</h1>
            </div>
            <p className="text-sm mt-1" style={{ color: "var(--yalola-text-muted)" }}>
              {t("ads.manager_subtitle", "Crée et suis tes campagnes sponsorisées sur Yalola.")}
            </p>
          </div>
          <Button className="pill pill-primary" onClick={() => setShowForm((s) => !s)} data-testid="ad-new-btn">
            <Plus size={16} className="mr-1" /> {t("ads.new_campaign", "Nouvelle campagne")}
          </Button>
        </div>
        )}

        {!isSuper && !formScreen && (
          <AdCreditBalance balance={creditBalance} currency={creditCur} loading={!credits} className="yalola-card" />
        )}
        {!isSuper && !formScreen && credits && lowBalanceAds.length > 0 && (
          <div className="yalola-card p-4 flex items-start gap-3 flex-wrap" style={{ borderColor: "#FDE68A", background: "#FFFBEB" }} data-testid="ads-low-balance-banner">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" style={{ color: "#B45309" }} />
            <div className="flex-1 min-w-[200px]">
              <div className="text-sm font-bold" style={{ color: "#92400E" }}>{t("ads.low_balance_title", "Solde bas")}</div>
              <p className="text-xs mt-0.5" style={{ color: "#92400E" }} data-testid="ads-low-balance-body">
                {t("ads.low_balance_body", "« {title} » se termine {when} : ton solde ({balance}) ne couvre pas une journée de plus (≈ {daily}/jour).")
                  .replace("{title}", lowBalanceAds[0].title || "")
                  .replace("{when}", whenLabel(lowBalanceAds[0].expires_at))
                  .replace("{balance}", fmtMoney(creditBalance, creditCur, lang))
                  .replace("{daily}", fmtMoney(lowBalanceAds[0].daily, creditCur, lang))}
                {lowBalanceAds.length > 1 && <span className="block mt-0.5">{t("ads.low_balance_more", "+ {n} autre(s) campagne(s) concernée(s)").replace("{n}", String(lowBalanceAds.length - 1))}</span>}
              </p>
            </div>
            <Button className="pill pill-primary h-9" onClick={() => navigate("/ads/credits")} data-testid="ads-low-balance-recharge">
              <Plus size={14} className="mr-1" /> {t("adcredit.topup", "Recharger")}
            </Button>
          </div>
        )}

        {!formScreen && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ads-stats">
          <StatCard label={t("ads.stat_campaigns", "Campagnes")} value={ads.length} color="var(--yalola-primary)" />
          <StatCard label={t("ads.stat_impressions", "Impressions")} value={totalImpr} />
          <StatCard label={t("ads.stat_clicks", "Clics")} value={totalClicks} color="var(--yalola-secondary)" />
          <StatCard label={t("ads.stat_ctr", "CTR moyen")} value={`${ctr}%`} color="var(--yalola-accent)" />
          <StatCard label={t("ads.stat_leads", "Prospects")} value={totalLeads} color="var(--yalola-primary)" />
        </div>
        )}

        {showForm && !isMobile && (
          <div className="grid lg:grid-cols-[1fr_360px] gap-4 items-start" data-testid="ad-create-wrap">
          <div className="yalola-card p-5 space-y-3" data-testid="ad-create-form">
            <div className="font-bold font-display text-lg">{t("ads.new_campaign", "Nouvelle campagne")}</div>
            {isStaff && (
              <div className="rounded-xl border p-3 space-y-3" style={{ borderColor: "var(--yalola-primary)", background: "var(--yalola-primary-soft)" }} data-testid="ad-offline-section">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" className="mt-1" checked={form.offline} onChange={(e) => setForm({ ...form, offline: e.target.checked })} data-testid="ad-offline-toggle" />
                  <span>
                    <span className="text-sm font-bold block">{t("ads.offline_section", "Pub hors-ligne (admin)")}</span>
                    <span className="text-[11px] block" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.offline_hint", "Publier une pub au nom d'un annonceur externe ou d'une église, facturée hors-ligne (virement, espèces, PayPal…) ou offerte. Aucun paiement en ligne.")}</span>
                  </span>
                </label>
                {form.offline && (
                  <div className="space-y-3 pt-1">
                    <div>
                      <Label>{t("ads.advertiser_name_label", "Nom de l'annonceur (personnalisé)")}</Label>
                      <Input value={form.advertiser_name} onChange={(e) => setForm({ ...form, advertiser_name: e.target.value, as_church_id: "" })} placeholder={t("ads.advertiser_name_ph", "Ex. Boulangerie Emmanuel")} disabled={!!form.as_church_id} data-testid="ad-offline-advertiser" />
                    </div>
                    {myChurches.length > 0 && (
                      <div>
                        <Label>{t("ads.as_church_label", "Ou au nom d'une église")}</Label>
                        <select value={form.as_church_id} onChange={(e) => setForm({ ...form, as_church_id: e.target.value, advertiser_name: e.target.value ? "" : form.advertiser_name })} className="w-full h-10 rounded-xl border px-3 text-sm bg-white" style={{ borderColor: "var(--yalola-border)" }} data-testid="ad-offline-church">
                          <option value="">{t("ads.as_church_none", "— Aucune (nom personnalisé) —")}</option>
                          {myChurches.map((c) => <option key={c.id} value={c.id}>{c.name}{c.city ? ` — ${c.city}` : ""}</option>)}
                        </select>
                      </div>
                    )}
                    <div>
                      <Label>{t("ads.billing_mode_label", "Mode de facturation")} *</Label>
                      <select value={form.billing_mode} onChange={(e) => setForm({ ...form, billing_mode: e.target.value, manual_price: e.target.value === "free" ? "" : form.manual_price })} className="w-full h-10 rounded-xl border px-3 text-sm bg-white" style={{ borderColor: "var(--yalola-border)" }} data-testid="ad-offline-billing-mode">
                        <option value="">{t("ads.billing_select_ph", "— Choisir —")}</option>
                        {AD_BILLING_MODES.map((m) => <option key={m} value={m}>{t(`ads.billing_${m}`, m)}</option>)}
                      </select>
                      {form.billing_mode === "free" && <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="ad-offline-billing-free-hint">{t("ads.billing_free_hint", "Offerte — aucun revenu enregistré.")}</p>}
                    </div>
                    {form.billing_mode !== "free" && (
                      <div>
                        <Label>{t("ads.manual_price_label", "Montant facturé (comptabilité)")} *</Label>
                        <Input type="number" min="0.01" step="0.01" value={form.manual_price} onChange={(e) => setForm({ ...form, manual_price: e.target.value })} placeholder={t("ads.manual_price_ph", "Ex. 50")} data-testid="ad-offline-price" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div>
              <Label>{t("ads.title_label", "Titre")} *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("ads.title_ph", "Ex. Concert de louange 2026")} data-testid="ad-title" />
            </div>
            <div>
              <Label>{t("ads.desc_label", "Description")}</Label>
              <Textarea rows={2} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder={t("ads.desc_ph", "Décris ton offre...")} data-testid="ad-body" />
            </div>
            <div>
              <Label>{t("ads.visual_label", "Visuel (image ou vidéo)")} *</Label>
              {form.media_url ? (
                <div className="relative mt-1 rounded-xl overflow-hidden border" style={{ borderColor: "var(--yalola-border)" }}>
                  {form.media_type === "video"
                    ? <VideoPlayer mode="controls" src={form.media_url} poster={form.poster_url} className="w-full aspect-video" testId="ad-media-preview-video" />
                    : <img src={resolveMediaUrl(form.media_url)} onError={onMediaImgError} alt="" className="w-full max-h-[70vh] object-contain bg-black/5" />}
                  <button onClick={() => setForm({ ...form, media_url: "" })} className="absolute top-2 right-2 rounded-full bg-black/60 text-white px-2 py-1 text-xs" data-testid="ad-media-remove">{t("ads.visual_remove", "Retirer")}</button>
                </div>
              ) : (
                <div className="mt-1 flex flex-col sm:flex-row gap-2">
                  <ImageCropUpload aspectOptions={AD_ASPECTS} icon={ImagePlus} buttonLabel={t("ads.visual_add_image", "Ajouter une image")} title={t("ads.image_adjust", "Ajuster l'image")} onUploaded={(url) => setForm((f) => ({ ...f, media_url: url, media_type: "image", poster_url: "" }))} testId="ad-media-image" />
                  <FileUpload accept="video/*" onUploaded={onMedia} variant="compact" context="ad" icon={Video} capturePoster buttonLabel={t("ads.visual_add_video", "Ajouter une vidéo")} testId="ad-media-video" />
                </div>
              )}
            </div>

            {/* Bouton d'action (CTA) façon Facebook */}
            {/* Bouton d'action (CTA) façon Facebook */}
            <div className="rounded-xl border p-3 space-y-3" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }} data-testid="ad-cta-config">
              <div className="text-sm font-bold">{t("ads.cta_section", "Bouton d'action (comme sur Facebook)")}</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>{t("ads.cta_type_label", "Type de bouton")}</Label>
                  <select value={form.cta_type} onChange={(e) => setForm({ ...form, cta_type: e.target.value })} className="w-full h-10 rounded-xl border px-3 text-sm bg-white" style={{ borderColor: "var(--yalola-border)" }} data-testid="ad-cta-type">
                    {CTA_TYPES.map((o) => <option key={o.value} value={o.value}>{t(`ads.ctatype_${o.value}`, o.label)}</option>)}
                  </select>
                </div>
                {form.cta_type !== "none" && (
                  <div>
                    <Label>{t("ads.cta_label_label", "Libellé du bouton")}</Label>
                    <select value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} className="w-full h-10 rounded-xl border px-3 text-sm bg-white" style={{ borderColor: "var(--yalola-border)" }} data-testid="ad-cta-label">
                      {CTA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(`ads.ctalabel_${o.value}`, o.label)}</option>)}
                    </select>
                  </div>
                )}
              </div>
              {form.cta_type === "url" && (
                <div><Label>{t("ads.cta_link_label", "Lien du bouton")}</Label><Input value={form.cta_value} onChange={(e) => setForm({ ...form, cta_value: e.target.value })} placeholder={t("ads.cta_link_ph", "https://votre-site.com/devis")} data-testid="ad-cta-value" /></div>
              )}
              {form.cta_type === "whatsapp" && (
                <div><Label>{t("ads.cta_whatsapp_label", "Numéro WhatsApp (indicatif compris)")}</Label><Input value={form.cta_value} onChange={(e) => setForm({ ...form, cta_value: e.target.value })} placeholder={t("ads.phone_ph", "+32470000000")} data-testid="ad-cta-value" /></div>
              )}
              {form.cta_type === "phone" && (
                <div><Label>{t("ads.cta_phone_label", "Numéro de téléphone")}</Label><Input value={form.cta_value} onChange={(e) => setForm({ ...form, cta_value: e.target.value })} placeholder={t("ads.phone_ph", "+32470000000")} data-testid="ad-cta-value" /></div>
              )}
              {form.cta_type === "lead_form" && (
                <div>
                  <Label>{t("ads.lead_fields_label", "Champs du formulaire à collecter")}</Label>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {LEAD_FIELD_OPTS.map((f) => {
                      const on = form.lead_fields.includes(f.key);
                      return (
                        <button key={f.key} type="button" onClick={() => toggleLeadField(f.key)} className={`pill ${on ? "pill-primary" : "pill-outline"}`} data-testid={`ad-lead-field-${f.key}`}>
                          {f.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {form.cta_type === "lead_form" && (
                <div data-testid="ad-custom-fields">
                  <div className="flex items-center justify-between">
                    <Label>{t("ads.custom_q_label", "Questions personnalisées")} <span className="font-normal" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.custom_q_max", "(max 5)")}</span></Label>
                    {form.custom_fields.length < 5 && (
                      <button type="button" onClick={addCustomField} className="text-xs font-semibold inline-flex items-center gap-1" style={{ color: "var(--yalola-primary)" }} data-testid="ad-custom-add">
                        <Plus size={13} /> {t("ads.custom_q_add", "Ajouter une question")}
                      </button>
                    )}
                  </div>
                  <div className="space-y-2 mt-1.5">
                    {form.custom_fields.length === 0 && (
                      <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.custom_q_hint", "Ajoutez vos propres questions (texte, nombre ou liste déroulante).")}</p>
                    )}
                    {form.custom_fields.map((c, i) => (
                      <div key={i} className="rounded-xl border p-2.5 space-y-2 bg-white" style={{ borderColor: "var(--yalola-border)" }} data-testid={`ad-custom-field-${i}`}>
                        <div className="flex items-center gap-2">
                          <Input value={c.label} onChange={(e) => updateCustomField(i, { label: e.target.value })} placeholder={t("ads.custom_q_ph", "Intitulé de la question")} className="flex-1" data-testid={`ad-custom-label-${i}`} />
                          <select value={c.type} onChange={(e) => updateCustomField(i, { type: e.target.value })} className="h-10 rounded-xl border px-2 text-sm bg-white shrink-0" style={{ borderColor: "var(--yalola-border)" }} data-testid={`ad-custom-type-${i}`}>
                            <option value="text">{t("ads.custom_type_text", "Texte")}</option>
                            <option value="number">{t("ads.custom_type_number", "Nombre")}</option>
                            <option value="select">{t("ads.custom_type_select", "Liste déroulante")}</option>
                          </select>
                          <button type="button" onClick={() => removeCustomField(i)} className="h-9 w-9 grid place-items-center rounded-lg hover:bg-red-50 text-red-600 shrink-0" data-testid={`ad-custom-remove-${i}`}><Trash2 size={15} /></button>
                        </div>
                        {c.type === "select" && (
                          <Input value={c.options} onChange={(e) => updateCustomField(i, { options: e.target.value })} placeholder={t("ads.custom_options_ph", "Options séparées par des virgules (ex : Oui, Non, Peut-être)")} data-testid={`ad-custom-options-${i}`} />
                        )}
                        <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: "var(--yalola-text-muted)" }}>
                          <input type="checkbox" checked={c.required} onChange={(e) => updateCustomField(i, { required: e.target.checked })} data-testid={`ad-custom-required-${i}`} /> {t("ads.custom_required", "Obligatoire")}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>{t("ads.cta_headline_label", "Titre du bouton (gras)")}</Label><Input value={form.cta_headline} onChange={(e) => setForm({ ...form, cta_headline: e.target.value })} placeholder={t("ads.cta_headline_ph", "Concert de louange — Liège")} data-testid="ad-cta-headline" /></div>
                <div><Label>{t("ads.cta_subtitle_label", "Sous-titre")}</Label><Input value={form.cta_description} onChange={(e) => setForm({ ...form, cta_description: e.target.value })} placeholder={t("ads.cta_subtitle_ph", "Entrée libre · samedi 20 h")} data-testid="ad-cta-description" /></div>
              </div>
            </div>

            {/* Ciblage intelligent (facultatif) */}
            {/* Ciblage intelligent (facultatif) */}
            <div className="rounded-xl border p-3" style={{ borderColor: "var(--yalola-border)" }} data-testid="ad-targeting-config">
              <button type="button" onClick={() => setShowTargeting((v) => !v)} className="w-full flex items-center justify-between text-sm font-bold" data-testid="ad-targeting-toggle">
                <span className="flex items-center gap-1.5"><Target size={15} /> {t("ads.targeting_toggle", "Affiner l'audience (facultatif)")}</span>
                <span className="text-lg leading-none">{showTargeting ? "−" : "+"}</span>
              </button>
              {showTargeting && (
                <div className="space-y-3 mt-3">
                  <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
                    {t("ads.targeting_hint", "Laissez vide pour toucher tout le monde. Sexe, âge et pays filtrent l'audience ; villes, églises, obédiences et centres d'intérêt donnent la priorité aux membres concernés.")}
                  </p>
                  <div>
                    <Label>{t("ads.gender_label", "Sexe")}</Label>
                    <div className="flex gap-2 mt-1">
                      {[["male", t("ads.gender_male", "Hommes")], ["female", t("ads.gender_female", "Femmes")]].map(([v, l]) => (
                        <button type="button" key={v} onClick={() => toggleArr("t_genders", v)} className={`pill ${form.t_genders.includes(v) ? "pill-primary" : "pill-outline"}`} data-testid={`ad-target-gender-${v}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label>{t("ads.age_ranges", "Tranches d'âge")}</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1" data-testid="ad-target-age-ranges">
                      <button type="button" onClick={() => setAgeRanges([])} className={`pill text-xs ${form.t_age_ranges.length === 0 ? "pill-primary" : "pill-outline"}`} data-testid="ad-age-range-all">{t("ads.age_all", "Tous les âges")}</button>
                      {AGE_RANGES.map((r) => (
                        <button type="button" key={r.label} onClick={() => setAgeRanges(form.t_age_ranges.includes(r.label) ? form.t_age_ranges.filter((x) => x !== r.label) : [...form.t_age_ranges, r.label])} className={`pill text-xs ${form.t_age_ranges.includes(r.label) ? "pill-primary" : "pill-outline"}`} data-testid={`ad-age-range-${r.label}`}>{r.label}</button>
                      ))}
                    </div>
                    {(Number(form.t_age_min) > 0 || Number(form.t_age_max) > 0) && (
                      <p className="text-[11px] mt-1 font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid="ad-age-result">
                        {Number(form.t_age_max) > 0
                          ? t("ads.age_result", "{min} – {max} ans").replace("{min}", String(Number(form.t_age_min) || 13)).replace("{max}", String(form.t_age_max))
                          : t("ads.age_result_min", "{min} ans et plus").replace("{min}", String(form.t_age_min))}
                      </p>
                    )}
                    {!isMobile && (
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div><Label className="text-xs">{t("ads.age_min", "Âge min")}</Label><Input type="number" min="0" value={form.t_age_min} onChange={(e) => setForm({ ...form, t_age_min: e.target.value, t_age_ranges: [] })} placeholder="0" data-testid="ad-target-age-min" /></div>
                        <div><Label className="text-xs">{t("ads.age_max", "Âge max")}</Label><Input type="number" min="0" value={form.t_age_max} onChange={(e) => setForm({ ...form, t_age_max: e.target.value, t_age_ranges: [] })} placeholder={t("ads.age_max_ph", "0 = aucun")} data-testid="ad-target-age-max" /></div>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>{t("ads.countries_label", "Pays (virgules)")}</Label><Input value={form.t_countries} onChange={(e) => setForm({ ...form, t_countries: e.target.value })} placeholder="BE, FR, CD" data-testid="ad-target-countries" /></div>
                    <div><Label>{t("ads.cities_label", "Villes (virgules)")}</Label><Input value={form.t_cities} onChange={(e) => setForm({ ...form, t_cities: e.target.value })} placeholder="Bruxelles, Paris" data-testid="ad-target-cities" /></div>
                  </div>
                  <div className="relative">
                    <Label>{t("ads.churches_label", "Églises ciblées (priorité)")}</Label>
                    <Input value={churchQuery} onChange={(e) => setChurchQuery(e.target.value)} placeholder={t("ads.churches_ph", "Rechercher une église…")} data-testid="ad-target-church-search" />
                    {churchResults.length > 0 && (
                      <div className="mt-1 rounded-xl border overflow-hidden bg-white z-10" style={{ borderColor: "var(--yalola-border)" }} data-testid="ad-target-church-results">
                        {churchResults.map((c) => (
                          <button type="button" key={c.id} onClick={() => addChurch(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-black/5 flex items-center justify-between gap-2" data-testid={`ad-target-church-opt-${c.id}`}>
                            <span className="truncate">{c.name}</span>
                            {c.city && <span className="text-xs shrink-0" style={{ color: "var(--yalola-text-muted)" }}>{c.city}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                    {form.t_churches.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="ad-target-churches-selected">
                        {form.t_churches.map((c) => (
                          <button type="button" key={c.id} onClick={() => removeChurch(c.id)} className="pill pill-primary text-xs flex items-center gap-1" data-testid={`ad-target-church-chip-${c.id}`}>{c.name} <X size={11} /></button>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.churches_hint", "Les membres de ces églises verront la pub en priorité.")}</p>
                  </div>
                  {denominations.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <Label>{t("ads.denominations_label", "Obédiences ciblées")}</Label>
                        <div className="flex items-center gap-3">
                          <button type="button" onClick={() => setForm((f) => ({ ...f, t_denominations: denominations.map((d) => d.name) }))} className="text-[11px] font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid="ad-denom-select-all">{t("ads.select_all", "Tout sélectionner")}</button>
                          {form.t_denominations.length > 0 && (
                            <button type="button" onClick={() => setForm((f) => ({ ...f, t_denominations: [] }))} className="text-[11px] font-semibold" style={{ color: "var(--yalola-text-muted)" }} data-testid="ad-denom-clear-all">{t("ads.deselect_all", "Tout désélectionner")}</button>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1 max-h-28 overflow-y-auto" data-testid="ad-target-denominations">
                        {denominations.map((d) => (
                          <button type="button" key={d.id} onClick={() => toggleArr("t_denominations", d.name)} className={`pill text-xs ${form.t_denominations.includes(d.name) ? "pill-primary" : "pill-outline"}`}>{d.name}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {interests.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <Label>{t("ads.interests_label", "Centres d'intérêt")}</Label>
                        <div className="flex items-center gap-3">
                          <button type="button" onClick={() => setForm((f) => ({ ...f, t_interests: interests.map((i) => i.key) }))} className="text-[11px] font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid="ad-interest-select-all">{t("ads.select_all", "Tout sélectionner")}</button>
                          {form.t_interests.length > 0 && (
                            <button type="button" onClick={() => setForm((f) => ({ ...f, t_interests: [] }))} className="text-[11px] font-semibold" style={{ color: "var(--yalola-text-muted)" }} data-testid="ad-interest-clear-all">{t("ads.deselect_all", "Tout désélectionner")}</button>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1 max-h-28 overflow-y-auto" data-testid="ad-target-interests">
                        {interests.map((i) => (
                          <button type="button" key={i.key} onClick={() => toggleArr("t_interests", i.key)} className={`pill text-xs ${form.t_interests.includes(i.key) ? "pill-primary" : "pill-outline"}`}>{i.labels?.[lang] || i.key}</button>
                        ))}
                      </div>
                      <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.interests_theme_hint", "Ex. Louange, Jeunesse, Prière, Évangélisation — votre pub sera montrée en priorité aux membres partageant ces thèmes.")}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ad-budget-section">
              <div>
                <Label>{t("ads.budget_label", "Budget ($) — min 1 $").replace(/\$/g, curSym)}</Label>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  {budgetPresets.map((b) => (
                    <button type="button" key={b} onClick={() => setForm((f) => ({ ...f, budget: b }))} className={`pill text-xs ${Number(form.budget) === b ? "pill-primary" : "pill-outline"}`} data-testid={`ad-budget-preset-${b}`}>{b} {curSym}</button>
                  ))}
                  <Input type="number" min="1" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="w-24 h-8" data-testid="ad-budget" />
                </div>
              </div>
              <div>
                <Label>{t("ads.days_label", "Durée (jours)")}</Label>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  {DAYS_PRESETS.map((d) => (
                    <button type="button" key={d} onClick={() => setForm((f) => ({ ...f, days: d }))} className={`pill text-xs ${Number(form.days) === d ? "pill-primary" : "pill-outline"}`} data-testid={`ad-days-preset-${d}`}>{d} {t("ads.days_short", "j")}</button>
                  ))}
                  <Input type="number" min="1" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} className="w-20 h-8" data-testid="ad-days" />
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-1.5"><CalendarClock size={14} /> {t("ads.starts_label", "Démarrer le… (facultatif)")}</Label>
                <Input type="datetime-local" value={form.starts_at} min={minStart} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="ad-starts-at" />
                <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.starts_hint", "Vide = dès la validation. Idéal pour un événement : prépare la pub à l'avance, elle partira au jour J.")}</p>
              </div>
              <div><Label>{t("ads.dest_link_label", "Lien de destination (facultatif)")}</Label><Input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="https://..." data-testid="ad-target" /></div>
            </div>
            {!payFree && (
              <AdCreditBalance balance={creditBalance} currency={creditCur} need={Number(form.budget) || 0} loading={!credits} />
            )}
            {estimate && !form.offline && (
              <div className="rounded-2xl p-4 mt-1" style={{ background: "var(--yalola-primary-soft)", border: "1px solid var(--yalola-border)" }} data-testid="ad-estimate-box">
                <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--yalola-primary)" }}>
                  <Users size={16} /> {t("ads.estimate_reach", "Portée estimée")}
                </div>
                <div className="mt-1 text-sm">
                  {t("ads.estimate_audience_pre", "Votre ciblage touche en priorité")} <b data-testid="ad-estimate-audience">~{(estimate.audience || 0).toLocaleString("fr-FR")}</b> {t("ads.estimate_members", "membres")}.
                </div>
                <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-[13px]" style={{ color: "var(--yalola-text-muted)" }}>
                    {t("ads.estimate_reco", "Recommandé")} : <b>{estimate.recommended_budget} {curSym}</b> {t("ads.estimate_over", "sur")} <b>{estimate.recommended_days} {t("ads.days_short", "j")}</b>
                  </div>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, budget: String(estimate.recommended_budget), days: estimate.recommended_days }))} className="pill pill-outline text-xs" data-testid="ad-estimate-apply">
                    {t("ads.estimate_apply", "Appliquer")}
                  </button>
                </div>

                {estimate.target_impressions > 0 && (
                  <div className="mt-2 rounded-lg px-2.5 py-2 text-[12px] flex items-start gap-1.5" style={{ background: "var(--yalola-surface, #fff)", border: "1px solid var(--yalola-border)" }} data-testid="ad-estimate-objective">
                    <TrendingUp size={14} className="mt-0.5 shrink-0" style={{ color: "var(--yalola-primary)" }} />
                    <span><b>{t("ads.objective", "Objectif")}</b> : {t("ads.objective_line", "~{n} impressions sur {days} j (≈ {daily}/jour)").replace("{n}", estimate.target_impressions.toLocaleString("fr-FR")).replace("{days}", String(Number(form.days) || 7)).replace("{daily}", (estimate.daily_impressions || 0).toLocaleString("fr-FR"))}</span>
                  </div>
                )}

                {estimate.audience > 0 && estimate.audience < 20 && (
                  <div className="rounded-lg px-2.5 py-2 mt-2 text-[12px] flex items-start gap-1.5" style={{ background: "rgba(245,158,11,0.15)", color: "#b45309" }} data-testid="ad-audience-narrow-warning">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>{t("ads.audience_too_narrow", "Audience trop étroite : élargissez votre ciblage (âge, pays, obédiences…) pour toucher plus de membres.")}</span>
                  </div>
                )}

                {(estimate.breakdown || []).some((b) => (b.total || 0) > 0) && (() => {
                  const bd = estimate.breakdown;
                  const bdMax = Math.max(1, ...bd.map((b) => b.total || 0));
                  return (
                    <div className="mt-3 rounded-xl p-3" style={{ background: "var(--yalola-surface, #fff)", border: "1px solid var(--yalola-border)" }} data-testid="ad-audience-breakdown">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.breakdown_title", "Répartition par âge")}</span>
                        <div className="flex items-center gap-2.5 text-[10px]" style={{ color: "var(--yalola-text-muted)" }}>
                          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full" style={{ background: "var(--yalola-primary)" }} />{t("ads.breakdown_men", "Hommes")}</span>
                          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full" style={{ background: "#ec4899" }} />{t("ads.breakdown_women", "Femmes")}</span>
                        </div>
                      </div>
                      <div className="flex items-end justify-between gap-1.5" style={{ height: 88 }}>
                        {bd.map((b) => (
                          <div key={b.label} className="flex-1 flex flex-col items-center gap-1 min-w-0" data-testid={`ad-breakdown-bar-${b.label}`}>
                            <div className="w-full flex items-end justify-center gap-[3px]" style={{ height: 64 }}>
                              <div className="w-2 rounded-t transition-[height] duration-300" title={`${b.male} · ${b.label}`} style={{ height: `${Math.round((b.male / bdMax) * 100)}%`, minHeight: b.male > 0 ? 3 : 0, background: "var(--yalola-primary)" }} />
                              <div className="w-2 rounded-t transition-[height] duration-300" title={`${b.female} · ${b.label}`} style={{ height: `${Math.round((b.female / bdMax) * 100)}%`, minHeight: b.female > 0 ? 3 : 0, background: "#ec4899" }} />
                            </div>
                            <span className="text-[8px] leading-none" style={{ color: "var(--yalola-text-muted)" }}>{b.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
            <div className="flex items-center gap-2 justify-end pt-1 flex-wrap">
              <span className="text-[11px] mr-auto" style={{ color: "var(--yalola-text-muted)" }} data-testid="ad-draft-hint">{t("ads.draft_saved", "Brouillon enregistré automatiquement")}</span>
              <Button variant="outline" className="rounded-full" onClick={cancelForm}>{t("ads.cancel", "Annuler")}</Button>
              <Button className="pill pill-primary" onClick={create} disabled={creating || (!payFree && (Number(form.budget) || 0) > creditBalance)} data-testid="ad-create-submit">
                {creating ? t("ads.launching", "Création...") : (isStaff && form.offline) ? t("ads.offline_create", "Créer la pub (hors-ligne)") : t("ads.launch", "Lancer la campagne")}
              </Button>
            </div>
          </div>
          <div className="lg:sticky lg:top-20">
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.preview_title", "Aperçu en direct")}</div>
              <div className="inline-flex rounded-full p-0.5" style={{ background: "var(--yalola-surface-alt)" }} data-testid="ad-preview-placement-toggle">
                <button type="button" onClick={() => setPreviewMode("feed")} className="px-3 py-1 rounded-full text-xs font-semibold transition-colors" style={previewMode === "feed" ? { background: "var(--yalola-primary)", color: "#fff" } : { color: "var(--yalola-text-muted)", background: "transparent" }} data-testid="ad-preview-mode-feed">{t("ads.placement_feed", "Fil")}</button>
                <button type="button" onClick={() => setPreviewMode("reels")} className="px-3 py-1 rounded-full text-xs font-semibold transition-colors" style={previewMode === "reels" ? { background: "var(--yalola-primary)", color: "#fff" } : { color: "var(--yalola-text-muted)", background: "transparent" }} data-testid="ad-preview-mode-reels">{t("ads.placement_reels", "Reels")}</button>
              </div>
            </div>
            {previewMode === "reels" ? <ReelsAdPreview form={form} user={user} /> : <AdPreview form={form} user={user} />}
          </div>
          </div>
        )}

        {showForm && isMobile && (
          <div ref={formRef} className="yalola-card p-4 space-y-3 pb-6" style={{ scrollMarginTop: "calc(3.5rem + var(--sa-top, 0px) + 0.5rem)" }} data-testid="ad-create-form" data-step={step}>
            <div className="flex items-center justify-between gap-2">
              <div className="font-bold font-display text-lg" data-testid="ad-form-title">{t("ads.new_campaign", "Nouvelle campagne")}</div>
              <span className="text-xs font-semibold" style={{ color: "var(--yalola-text-muted)" }} data-testid="ad-step-label">{t("ads.step_of", "Étape {n} sur 3").replace("{n}", String(step))}</span>
            </div>
            <div className="grid grid-cols-3 gap-1.5" data-testid="ad-steps">
              {STEP_KEYS.map((k, i) => (
                <button type="button" key={k} onClick={() => (i + 1 < step || stepValid(i + 1)) && goStep(i + 1)} className="text-left" data-testid={`ad-step-${i + 1}`} data-active={step === i + 1 ? "1" : "0"}>
                  <div className="h-1.5 rounded-full" style={{ background: step >= i + 1 ? "var(--yalola-primary)" : "var(--yalola-border)" }} />
                  <div className="text-[11px] mt-1 font-semibold leading-tight" style={{ color: step === i + 1 ? "var(--yalola-text)" : "var(--yalola-text-muted)" }}>{t(`ads.step_${i + 1}`, k)}</div>
                </button>
              ))}
            </div>
            {step === 1 && (<>
            {isStaff && (
              <div className="rounded-xl border p-3 space-y-3" style={{ borderColor: "var(--yalola-primary)", background: "var(--yalola-primary-soft)" }} data-testid="ad-offline-section">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" className="mt-1" checked={form.offline} onChange={(e) => setForm({ ...form, offline: e.target.checked })} data-testid="ad-offline-toggle" />
                  <span>
                    <span className="text-sm font-bold block">{t("ads.offline_section", "Pub hors-ligne (admin)")}</span>
                    <span className="text-[11px] block" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.offline_hint", "Publier une pub au nom d'un annonceur externe ou d'une église, facturée hors-ligne (virement, espèces, PayPal…) ou offerte. Aucun paiement en ligne.")}</span>
                  </span>
                </label>
                {form.offline && (
                  <div className="space-y-3 pt-1">
                    <div>
                      <Label>{t("ads.advertiser_name_label", "Nom de l'annonceur (personnalisé)")}</Label>
                      <Input value={form.advertiser_name} onChange={(e) => setForm({ ...form, advertiser_name: e.target.value, as_church_id: "" })} placeholder={t("ads.advertiser_name_ph", "Ex. Boulangerie Emmanuel")} disabled={!!form.as_church_id} data-testid="ad-offline-advertiser" />
                    </div>
                    {myChurches.length > 0 && (
                      <div>
                        <Label>{t("ads.as_church_label", "Ou au nom d'une église")}</Label>
                        <select value={form.as_church_id} onChange={(e) => setForm({ ...form, as_church_id: e.target.value, advertiser_name: e.target.value ? "" : form.advertiser_name })} className="w-full h-10 rounded-xl border px-3 text-sm bg-white" style={{ borderColor: "var(--yalola-border)" }} data-testid="ad-offline-church">
                          <option value="">{t("ads.as_church_none", "— Aucune (nom personnalisé) —")}</option>
                          {myChurches.map((c) => <option key={c.id} value={c.id}>{c.name}{c.city ? ` — ${c.city}` : ""}</option>)}
                        </select>
                      </div>
                    )}
                    <div>
                      <Label>{t("ads.billing_mode_label", "Mode de facturation")} *</Label>
                      <select value={form.billing_mode} onChange={(e) => setForm({ ...form, billing_mode: e.target.value, manual_price: e.target.value === "free" ? "" : form.manual_price })} className="w-full h-10 rounded-xl border px-3 text-sm bg-white" style={{ borderColor: "var(--yalola-border)" }} data-testid="ad-offline-billing-mode">
                        <option value="">{t("ads.billing_select_ph", "— Choisir —")}</option>
                        {AD_BILLING_MODES.map((m) => <option key={m} value={m}>{t(`ads.billing_${m}`, m)}</option>)}
                      </select>
                      {form.billing_mode === "free" && <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="ad-offline-billing-free-hint">{t("ads.billing_free_hint", "Offerte — aucun revenu enregistré.")}</p>}
                    </div>
                    {form.billing_mode !== "free" && (
                      <div>
                        <Label>{t("ads.manual_price_label", "Montant facturé (comptabilité)")} *</Label>
                        <Input type="number" min="0.01" step="0.01" value={form.manual_price} onChange={(e) => setForm({ ...form, manual_price: e.target.value })} placeholder={t("ads.manual_price_ph", "Ex. 50")} data-testid="ad-offline-price" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div>
              <Label>{t("ads.title_label", "Titre")} *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("ads.title_ph", "Ex. Concert de louange 2026")} data-testid="ad-title" />
            </div>
            <div>
              <Label>{t("ads.desc_label", "Description")}</Label>
              <Textarea rows={2} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder={t("ads.desc_ph", "Décris ton offre...")} data-testid="ad-body" />
            </div>
            <div>
              <Label>{t("ads.visual_label", "Visuel (image ou vidéo)")} *</Label>
              {form.media_url ? (
                <div className="relative mt-1 rounded-xl overflow-hidden border" style={{ borderColor: "var(--yalola-border)" }}>
                  {form.media_type === "video"
                    ? <VideoPlayer mode="controls" src={form.media_url} poster={form.poster_url} className="w-full aspect-video" testId="ad-media-preview-video" />
                    : <img src={resolveMediaUrl(form.media_url)} onError={onMediaImgError} alt="" className="w-full max-h-[70vh] object-contain bg-black/5" />}
                  <button onClick={() => setForm({ ...form, media_url: "" })} className="absolute top-2 right-2 rounded-full bg-black/60 text-white px-2 py-1 text-xs" data-testid="ad-media-remove">{t("ads.visual_remove", "Retirer")}</button>
                </div>
              ) : (
                <div className="mt-1 flex flex-col sm:flex-row gap-2">
                  <ImageCropUpload aspectOptions={AD_ASPECTS} icon={ImagePlus} buttonLabel={t("ads.visual_add_image", "Ajouter une image")} title={t("ads.image_adjust", "Ajuster l'image")} onUploaded={(url) => setForm((f) => ({ ...f, media_url: url, media_type: "image", poster_url: "" }))} testId="ad-media-image" />
                  <FileUpload accept="video/*" onUploaded={onMedia} variant="compact" context="ad" icon={Video} capturePoster buttonLabel={t("ads.visual_add_video", "Ajouter une vidéo")} testId="ad-media-video" />
                </div>
              )}
            </div>
            </>)}
            {step === 2 && (<>
            {/* Bouton d'action (CTA) façon Facebook */}
            <div className="rounded-xl border p-3 space-y-3" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }} data-testid="ad-cta-config">
              <div className="text-sm font-bold">{t("ads.cta_section", "Bouton d'action (comme sur Facebook)")}</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>{t("ads.cta_type_label", "Type de bouton")}</Label>
                  <select value={form.cta_type} onChange={(e) => setForm({ ...form, cta_type: e.target.value })} className="w-full h-10 rounded-xl border px-3 text-sm bg-white" style={{ borderColor: "var(--yalola-border)" }} data-testid="ad-cta-type">
                    {CTA_TYPES.map((o) => <option key={o.value} value={o.value}>{t(`ads.ctatype_${o.value}`, o.label)}</option>)}
                  </select>
                </div>
                {form.cta_type !== "none" && (
                  <div>
                    <Label>{t("ads.cta_label_label", "Libellé du bouton")}</Label>
                    <select value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} className="w-full h-10 rounded-xl border px-3 text-sm bg-white" style={{ borderColor: "var(--yalola-border)" }} data-testid="ad-cta-label">
                      {CTA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(`ads.ctalabel_${o.value}`, o.label)}</option>)}
                    </select>
                  </div>
                )}
              </div>
              {form.cta_type === "url" && (
                <div><Label>{t("ads.cta_link_label", "Lien du bouton")}</Label><Input value={form.cta_value} onChange={(e) => setForm({ ...form, cta_value: e.target.value })} placeholder={t("ads.cta_link_ph", "https://votre-site.com/devis")} data-testid="ad-cta-value" /></div>
              )}
              {form.cta_type === "whatsapp" && (
                <div><Label>{t("ads.cta_whatsapp_label", "Numéro WhatsApp (indicatif compris)")}</Label><Input value={form.cta_value} onChange={(e) => setForm({ ...form, cta_value: e.target.value })} placeholder={t("ads.phone_ph", "+32470000000")} data-testid="ad-cta-value" /></div>
              )}
              {form.cta_type === "phone" && (
                <div><Label>{t("ads.cta_phone_label", "Numéro de téléphone")}</Label><Input value={form.cta_value} onChange={(e) => setForm({ ...form, cta_value: e.target.value })} placeholder={t("ads.phone_ph", "+32470000000")} data-testid="ad-cta-value" /></div>
              )}
              {form.cta_type === "lead_form" && (
                <div>
                  <Label>{t("ads.lead_fields_label", "Champs du formulaire à collecter")}</Label>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {LEAD_FIELD_OPTS.map((f) => {
                      const on = form.lead_fields.includes(f.key);
                      return (
                        <button key={f.key} type="button" onClick={() => toggleLeadField(f.key)} className={`pill ${on ? "pill-primary" : "pill-outline"}`} data-testid={`ad-lead-field-${f.key}`}>
                          {f.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {form.cta_type === "lead_form" && (
                <div data-testid="ad-custom-fields">
                  <div className="flex items-center justify-between">
                    <Label>{t("ads.custom_q_label", "Questions personnalisées")} <span className="font-normal" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.custom_q_max", "(max 5)")}</span></Label>
                    {form.custom_fields.length < 5 && (
                      <button type="button" onClick={addCustomField} className="text-xs font-semibold inline-flex items-center gap-1" style={{ color: "var(--yalola-primary)" }} data-testid="ad-custom-add">
                        <Plus size={13} /> {t("ads.custom_q_add", "Ajouter une question")}
                      </button>
                    )}
                  </div>
                  <div className="space-y-2 mt-1.5">
                    {form.custom_fields.length === 0 && (
                      <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.custom_q_hint", "Ajoutez vos propres questions (texte, nombre ou liste déroulante).")}</p>
                    )}
                    {form.custom_fields.map((c, i) => (
                      <div key={i} className="rounded-xl border p-2.5 space-y-2 bg-white" style={{ borderColor: "var(--yalola-border)" }} data-testid={`ad-custom-field-${i}`}>
                        <div className="flex items-center gap-2">
                          <Input value={c.label} onChange={(e) => updateCustomField(i, { label: e.target.value })} placeholder={t("ads.custom_q_ph", "Intitulé de la question")} className="flex-1" data-testid={`ad-custom-label-${i}`} />
                          <select value={c.type} onChange={(e) => updateCustomField(i, { type: e.target.value })} className="h-10 rounded-xl border px-2 text-sm bg-white shrink-0" style={{ borderColor: "var(--yalola-border)" }} data-testid={`ad-custom-type-${i}`}>
                            <option value="text">{t("ads.custom_type_text", "Texte")}</option>
                            <option value="number">{t("ads.custom_type_number", "Nombre")}</option>
                            <option value="select">{t("ads.custom_type_select", "Liste déroulante")}</option>
                          </select>
                          <button type="button" onClick={() => removeCustomField(i)} className="h-9 w-9 grid place-items-center rounded-lg hover:bg-red-50 text-red-600 shrink-0" data-testid={`ad-custom-remove-${i}`}><Trash2 size={15} /></button>
                        </div>
                        {c.type === "select" && (
                          <Input value={c.options} onChange={(e) => updateCustomField(i, { options: e.target.value })} placeholder={t("ads.custom_options_ph", "Options séparées par des virgules (ex : Oui, Non, Peut-être)")} data-testid={`ad-custom-options-${i}`} />
                        )}
                        <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: "var(--yalola-text-muted)" }}>
                          <input type="checkbox" checked={c.required} onChange={(e) => updateCustomField(i, { required: e.target.checked })} data-testid={`ad-custom-required-${i}`} /> {t("ads.custom_required", "Obligatoire")}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>{t("ads.cta_headline_label", "Titre du bouton (gras)")}</Label><Input value={form.cta_headline} onChange={(e) => setForm({ ...form, cta_headline: e.target.value })} placeholder={t("ads.cta_headline_ph", "Concert de louange — Liège")} data-testid="ad-cta-headline" /></div>
                <div><Label>{t("ads.cta_subtitle_label", "Sous-titre")}</Label><Input value={form.cta_description} onChange={(e) => setForm({ ...form, cta_description: e.target.value })} placeholder={t("ads.cta_subtitle_ph", "Entrée libre · samedi 20 h")} data-testid="ad-cta-description" /></div>
              </div>
            </div>
            {/* Ciblage intelligent (facultatif) */}
            <div className="rounded-xl border p-3" style={{ borderColor: "var(--yalola-border)" }} data-testid="ad-targeting-config">
              <button type="button" onClick={() => setShowTargeting((v) => !v)} className="w-full flex items-center justify-between text-sm font-bold" data-testid="ad-targeting-toggle">
                <span className="flex items-center gap-1.5"><Target size={15} /> {t("ads.targeting_toggle", "Affiner l'audience (facultatif)")}</span>
                <span className="text-lg leading-none">{showTargeting ? "−" : "+"}</span>
              </button>
              {showTargeting && (
                <div className="space-y-3 mt-3">
                  <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
                    {t("ads.targeting_hint", "Laissez vide pour toucher tout le monde. Sexe, âge et pays filtrent l'audience ; villes, églises, obédiences et centres d'intérêt donnent la priorité aux membres concernés.")}
                  </p>
                  <div>
                    <Label>{t("ads.gender_label", "Sexe")}</Label>
                    <div className="flex gap-2 mt-1">
                      {[["male", t("ads.gender_male", "Hommes")], ["female", t("ads.gender_female", "Femmes")]].map(([v, l]) => (
                        <button type="button" key={v} onClick={() => toggleArr("t_genders", v)} className={`pill ${form.t_genders.includes(v) ? "pill-primary" : "pill-outline"}`} data-testid={`ad-target-gender-${v}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label>{t("ads.age_ranges", "Tranches d'âge")}</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1" data-testid="ad-target-age-ranges">
                      <button type="button" onClick={() => setAgeRanges([])} className={`pill text-xs ${form.t_age_ranges.length === 0 ? "pill-primary" : "pill-outline"}`} data-testid="ad-age-range-all">{t("ads.age_all", "Tous les âges")}</button>
                      {AGE_RANGES.map((r) => (
                        <button type="button" key={r.label} onClick={() => setAgeRanges(form.t_age_ranges.includes(r.label) ? form.t_age_ranges.filter((x) => x !== r.label) : [...form.t_age_ranges, r.label])} className={`pill text-xs ${form.t_age_ranges.includes(r.label) ? "pill-primary" : "pill-outline"}`} data-testid={`ad-age-range-${r.label}`}>{r.label}</button>
                      ))}
                    </div>
                    {(Number(form.t_age_min) > 0 || Number(form.t_age_max) > 0) && (
                      <p className="text-[11px] mt-1 font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid="ad-age-result">
                        {Number(form.t_age_max) > 0
                          ? t("ads.age_result", "{min} – {max} ans").replace("{min}", String(Number(form.t_age_min) || 13)).replace("{max}", String(form.t_age_max))
                          : t("ads.age_result_min", "{min} ans et plus").replace("{min}", String(form.t_age_min))}
                      </p>
                    )}
                    {!isMobile && (
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div><Label className="text-xs">{t("ads.age_min", "Âge min")}</Label><Input type="number" min="0" value={form.t_age_min} onChange={(e) => setForm({ ...form, t_age_min: e.target.value, t_age_ranges: [] })} placeholder="0" data-testid="ad-target-age-min" /></div>
                        <div><Label className="text-xs">{t("ads.age_max", "Âge max")}</Label><Input type="number" min="0" value={form.t_age_max} onChange={(e) => setForm({ ...form, t_age_max: e.target.value, t_age_ranges: [] })} placeholder={t("ads.age_max_ph", "0 = aucun")} data-testid="ad-target-age-max" /></div>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>{t("ads.countries_label", "Pays (virgules)")}</Label><Input value={form.t_countries} onChange={(e) => setForm({ ...form, t_countries: e.target.value })} placeholder="BE, FR, CD" data-testid="ad-target-countries" /></div>
                    <div><Label>{t("ads.cities_label", "Villes (virgules)")}</Label><Input value={form.t_cities} onChange={(e) => setForm({ ...form, t_cities: e.target.value })} placeholder="Bruxelles, Paris" data-testid="ad-target-cities" /></div>
                  </div>
                  <div className="relative">
                    <Label>{t("ads.churches_label", "Églises ciblées (priorité)")}</Label>
                    <Input value={churchQuery} onChange={(e) => setChurchQuery(e.target.value)} placeholder={t("ads.churches_ph", "Rechercher une église…")} data-testid="ad-target-church-search" />
                    {churchResults.length > 0 && (
                      <div className="mt-1 rounded-xl border overflow-hidden bg-white z-10" style={{ borderColor: "var(--yalola-border)" }} data-testid="ad-target-church-results">
                        {churchResults.map((c) => (
                          <button type="button" key={c.id} onClick={() => addChurch(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-black/5 flex items-center justify-between gap-2" data-testid={`ad-target-church-opt-${c.id}`}>
                            <span className="truncate">{c.name}</span>
                            {c.city && <span className="text-xs shrink-0" style={{ color: "var(--yalola-text-muted)" }}>{c.city}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                    {form.t_churches.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="ad-target-churches-selected">
                        {form.t_churches.map((c) => (
                          <button type="button" key={c.id} onClick={() => removeChurch(c.id)} className="pill pill-primary text-xs flex items-center gap-1" data-testid={`ad-target-church-chip-${c.id}`}>{c.name} <X size={11} /></button>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.churches_hint", "Les membres de ces églises verront la pub en priorité.")}</p>
                  </div>
                  {denominations.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <Label>{t("ads.denominations_label", "Obédiences ciblées")}</Label>
                        <div className="flex items-center gap-3">
                          <button type="button" onClick={() => setForm((f) => ({ ...f, t_denominations: denominations.map((d) => d.name) }))} className="text-[11px] font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid="ad-denom-select-all">{t("ads.select_all", "Tout sélectionner")}</button>
                          {form.t_denominations.length > 0 && (
                            <button type="button" onClick={() => setForm((f) => ({ ...f, t_denominations: [] }))} className="text-[11px] font-semibold" style={{ color: "var(--yalola-text-muted)" }} data-testid="ad-denom-clear-all">{t("ads.deselect_all", "Tout désélectionner")}</button>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1 max-h-28 overflow-y-auto" data-testid="ad-target-denominations">
                        {denominations.map((d) => (
                          <button type="button" key={d.id} onClick={() => toggleArr("t_denominations", d.name)} className={`pill text-xs ${form.t_denominations.includes(d.name) ? "pill-primary" : "pill-outline"}`}>{d.name}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {interests.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <Label>{t("ads.interests_label", "Centres d'intérêt")}</Label>
                        <div className="flex items-center gap-3">
                          <button type="button" onClick={() => setForm((f) => ({ ...f, t_interests: interests.map((i) => i.key) }))} className="text-[11px] font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid="ad-interest-select-all">{t("ads.select_all", "Tout sélectionner")}</button>
                          {form.t_interests.length > 0 && (
                            <button type="button" onClick={() => setForm((f) => ({ ...f, t_interests: [] }))} className="text-[11px] font-semibold" style={{ color: "var(--yalola-text-muted)" }} data-testid="ad-interest-clear-all">{t("ads.deselect_all", "Tout désélectionner")}</button>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1 max-h-28 overflow-y-auto" data-testid="ad-target-interests">
                        {interests.map((i) => (
                          <button type="button" key={i.key} onClick={() => toggleArr("t_interests", i.key)} className={`pill text-xs ${form.t_interests.includes(i.key) ? "pill-primary" : "pill-outline"}`}>{i.labels?.[lang] || i.key}</button>
                        ))}
                      </div>
                      <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.interests_theme_hint", "Ex. Louange, Jeunesse, Prière, Évangélisation — votre pub sera montrée en priorité aux membres partageant ces thèmes.")}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            </>)}
            {step === 3 && (<>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ad-budget-section">
              <div>
                <Label>{t("ads.budget_label", "Budget ($) — min 1 $").replace(/\$/g, curSym)}</Label>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  {budgetPresets.map((b) => (
                    <button type="button" key={b} onClick={() => setForm((f) => ({ ...f, budget: b }))} className={`pill text-xs ${Number(form.budget) === b ? "pill-primary" : "pill-outline"}`} data-testid={`ad-budget-preset-${b}`}>{b} {curSym}</button>
                  ))}
                  <Input type="number" min="1" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="w-24 h-8" data-testid="ad-budget" />
                </div>
              </div>
              <div>
                <Label>{t("ads.days_label", "Durée (jours)")}</Label>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  {DAYS_PRESETS.map((d) => (
                    <button type="button" key={d} onClick={() => setForm((f) => ({ ...f, days: d }))} className={`pill text-xs ${Number(form.days) === d ? "pill-primary" : "pill-outline"}`} data-testid={`ad-days-preset-${d}`}>{d} {t("ads.days_short", "j")}</button>
                  ))}
                  <Input type="number" min="1" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} className="w-20 h-8" data-testid="ad-days" />
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-1.5"><CalendarClock size={14} /> {t("ads.starts_label", "Démarrer le… (facultatif)")}</Label>
                <Input type="datetime-local" value={form.starts_at} min={minStart} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="ad-starts-at" />
                <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.starts_hint", "Vide = dès la validation. Idéal pour un événement : prépare la pub à l'avance, elle partira au jour J.")}</p>
              </div>
              <div><Label>{t("ads.dest_link_label", "Lien de destination (facultatif)")}</Label><Input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="https://..." data-testid="ad-target" /></div>
            </div>
            {!payFree && (
              <AdCreditBalance balance={creditBalance} currency={creditCur} need={Number(form.budget) || 0} loading={!credits} />
            )}
            {estimate && !form.offline && (
              <div className="rounded-2xl p-4 mt-1" style={{ background: "var(--yalola-primary-soft)", border: "1px solid var(--yalola-border)" }} data-testid="ad-estimate-box">
                <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--yalola-primary)" }}>
                  <Users size={16} /> {t("ads.estimate_reach", "Portée estimée")}
                </div>
                <div className="mt-1 text-sm">
                  {t("ads.estimate_audience_pre", "Votre ciblage touche en priorité")} <b data-testid="ad-estimate-audience">~{(estimate.audience || 0).toLocaleString("fr-FR")}</b> {t("ads.estimate_members", "membres")}.
                </div>
                <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-[13px]" style={{ color: "var(--yalola-text-muted)" }}>
                    {t("ads.estimate_reco", "Recommandé")} : <b>{estimate.recommended_budget} {curSym}</b> {t("ads.estimate_over", "sur")} <b>{estimate.recommended_days} {t("ads.days_short", "j")}</b>
                  </div>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, budget: String(estimate.recommended_budget), days: estimate.recommended_days }))} className="pill pill-outline text-xs" data-testid="ad-estimate-apply">
                    {t("ads.estimate_apply", "Appliquer")}
                  </button>
                </div>

                {estimate.target_impressions > 0 && (
                  <div className="mt-2 rounded-lg px-2.5 py-2 text-[12px] flex items-start gap-1.5" style={{ background: "var(--yalola-surface, #fff)", border: "1px solid var(--yalola-border)" }} data-testid="ad-estimate-objective">
                    <TrendingUp size={14} className="mt-0.5 shrink-0" style={{ color: "var(--yalola-primary)" }} />
                    <span><b>{t("ads.objective", "Objectif")}</b> : {t("ads.objective_line", "~{n} impressions sur {days} j (≈ {daily}/jour)").replace("{n}", estimate.target_impressions.toLocaleString("fr-FR")).replace("{days}", String(Number(form.days) || 7)).replace("{daily}", (estimate.daily_impressions || 0).toLocaleString("fr-FR"))}</span>
                  </div>
                )}

                {estimate.audience > 0 && estimate.audience < 20 && (
                  <div className="rounded-lg px-2.5 py-2 mt-2 text-[12px] flex items-start gap-1.5" style={{ background: "rgba(245,158,11,0.15)", color: "#b45309" }} data-testid="ad-audience-narrow-warning">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>{t("ads.audience_too_narrow", "Audience trop étroite : élargissez votre ciblage (âge, pays, obédiences…) pour toucher plus de membres.")}</span>
                  </div>
                )}

                {(estimate.breakdown || []).some((b) => (b.total || 0) > 0) && (() => {
                  const bd = estimate.breakdown;
                  const bdMax = Math.max(1, ...bd.map((b) => b.total || 0));
                  return (
                    <div className="mt-3 rounded-xl p-3" style={{ background: "var(--yalola-surface, #fff)", border: "1px solid var(--yalola-border)" }} data-testid="ad-audience-breakdown">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.breakdown_title", "Répartition par âge")}</span>
                        <div className="flex items-center gap-2.5 text-[10px]" style={{ color: "var(--yalola-text-muted)" }}>
                          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full" style={{ background: "var(--yalola-primary)" }} />{t("ads.breakdown_men", "Hommes")}</span>
                          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full" style={{ background: "#ec4899" }} />{t("ads.breakdown_women", "Femmes")}</span>
                        </div>
                      </div>
                      <div className="flex items-end justify-between gap-1.5" style={{ height: 88 }}>
                        {bd.map((b) => (
                          <div key={b.label} className="flex-1 flex flex-col items-center gap-1 min-w-0" data-testid={`ad-breakdown-bar-${b.label}`}>
                            <div className="w-full flex items-end justify-center gap-[3px]" style={{ height: 64 }}>
                              <div className="w-2 rounded-t transition-[height] duration-300" title={`${b.male} · ${b.label}`} style={{ height: `${Math.round((b.male / bdMax) * 100)}%`, minHeight: b.male > 0 ? 3 : 0, background: "var(--yalola-primary)" }} />
                              <div className="w-2 rounded-t transition-[height] duration-300" title={`${b.female} · ${b.label}`} style={{ height: `${Math.round((b.female / bdMax) * 100)}%`, minHeight: b.female > 0 ? 3 : 0, background: "#ec4899" }} />
                            </div>
                            <span className="text-[8px] leading-none" style={{ color: "var(--yalola-text-muted)" }}>{b.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
            </>)}
            <p className="text-[11px] text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="ad-draft-hint">{t("ads.draft_saved", "Brouillon enregistré automatiquement")}</p>
            <div className="fixed left-0 right-0 z-40 px-3 py-2 flex items-center gap-2 border-t bg-white" style={{ bottom: "calc(3.5rem + var(--sa-bottom, 0px))", borderColor: "var(--yalola-border)" }} data-testid="ad-wizard-bar">
              <Button type="button" variant="outline" className="rounded-full h-10 px-3" onClick={() => setPreviewOpen(true)} data-testid="ad-preview-open"><Eye size={16} /> <span className="ml-1">{t("ads.preview_btn", "Aperçu")}</span></Button>
              {step > 1
                ? <Button type="button" variant="outline" className="rounded-full h-10 px-3" onClick={() => goStep(step - 1)} data-testid="ad-step-back"><ChevronLeft size={16} /> {t("ads.back", "Retour")}</Button>
                : <Button type="button" variant="outline" className="rounded-full h-10 px-3" onClick={cancelForm} data-testid="ad-step-cancel">{t("ads.cancel", "Annuler")}</Button>}
              {step < 3
                ? <Button type="button" className="pill pill-primary flex-1 h-10" onClick={goNext} data-testid="ad-step-next">{t("ads.next", "Suivant")} <ChevronRight size={16} /></Button>
                : <Button type="button" className="pill pill-primary flex-1 h-10" onClick={create} disabled={creating || (!payFree && (Number(form.budget) || 0) > creditBalance)} data-testid="ad-create-submit">{creating ? t("ads.launching", "Création...") : (isStaff && form.offline) ? t("ads.offline_create", "Créer la pub (hors-ligne)") : t("ads.launch", "Lancer la campagne")}</Button>}
            </div>
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
              <DialogContent variant="sheet" data-testid="ad-preview-dialog">
                <DialogHeader>
                  <DialogTitle>{t("ads.preview_title", "Aperçu en direct")}</DialogTitle>
                  <DialogDescription className="sr-only">{t("ads.preview_title", "Aperçu en direct")}</DialogDescription>
                </DialogHeader>
                <DialogBody>
                  <div className="flex justify-end mb-2">
                    <div className="inline-flex rounded-full p-0.5" style={{ background: "var(--yalola-surface-alt)" }} data-testid="ad-preview-placement-toggle">
                      <button type="button" onClick={() => setPreviewMode("feed")} className="px-3 py-1 rounded-full text-xs font-semibold" style={previewMode === "feed" ? { background: "var(--yalola-primary)", color: "#fff" } : { color: "var(--yalola-text-muted)" }} data-testid="ad-preview-mode-feed">{t("ads.placement_feed", "Fil")}</button>
                      <button type="button" onClick={() => setPreviewMode("reels")} className="px-3 py-1 rounded-full text-xs font-semibold" style={previewMode === "reels" ? { background: "var(--yalola-primary)", color: "#fff" } : { color: "var(--yalola-text-muted)" }} data-testid="ad-preview-mode-reels">{t("ads.placement_reels", "Reels")}</button>
                    </div>
                  </div>
                  {previewMode === "reels" ? <ReelsAdPreview form={form} user={user} /> : <AdPreview form={form} user={user} />}
                </DialogBody>
              </DialogContent>
            </Dialog>
          </div>
        )}


        {ads.length === 0 && !showForm && (
          <div className="yalola-card p-10 text-center" data-testid="ads-empty">
            <Megaphone size={44} className="mx-auto mb-3" style={{ color: "var(--yalola-primary)" }} />
            <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
              {t("ads.empty", "Aucune campagne pour l'instant. Crée ta première publicité pour toucher la communauté Yalola.")}
            </p>
          </div>
        )}

        {!formScreen && (
        <div className="grid md:grid-cols-2 gap-3" data-testid="ads-list">
          {ads.map((a) => {
            const expired = new Date(a.expires_at) < now;
            const scheduled = !!a.starts_at && new Date(a.starts_at) > now;
            const c = a.impressions ? ((a.clicks / a.impressions) * 100).toFixed(1) : "0.0";
            const target = Number(a.target_impressions) || 0;
            const pct = target ? Math.min(100, Math.round(((a.impressions || 0) / target) * 100)) : 0;
            return (
              <div key={a.id} className="yalola-card overflow-hidden" data-testid={`ad-card-${a.id}`}>
                {a.media_url && (a.media_type === "video"
                  ? <VideoPlayer mode="controls" src={a.media_url} poster={a.poster_url} className="w-full h-40" testId={`ad-card-video-${a.id}`} />
                  : <img src={resolveMediaUrl(a.media_url)} onError={onMediaImgError} alt="" className="w-full h-40 object-contain bg-black/5" />)}
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold font-display truncate">{a.title}</div>
                    <span className={`chip text-[10px] ${a.active && !expired && !scheduled ? "chip-primary" : ""}`} style={a.payment_status === "pending" ? { background: "#FEF3C7", color: "#92400E", borderColor: "#FDE68A" } : scheduled ? { background: "#DBEAFE", color: "#1D4ED8", borderColor: "#BFDBFE" } : {}} data-testid={`ad-status-${a.id}`}>
                      {a.payment_status === "pending" ? t("ads.status_pending", "En attente de paiement") : scheduled ? t("ads.status_scheduled", "Programmée · {date}").replace("{date}", new Date(a.starts_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })) : expired ? t("ads.status_expired", "Expirée") : a.active ? t("ads.status_active", "Active") : t("ads.status_paused", "En pause")}
                    </span>
                  </div>
                  {a.body && <p className="text-xs mt-1 line-clamp-2" style={{ color: "var(--yalola-text-muted)" }}>{a.body}</p>}
                  {target > 0 && (
                    <div className="mt-2" data-testid={`ad-progress-${a.id}`}>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--yalola-border)" }}>
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "var(--yalola-primary)" }} />
                      </div>
                      <div className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.progress_impr", "{n} / {target} impressions").replace("{n}", (a.impressions || 0).toLocaleString("fr-FR")).replace("{target}", target.toLocaleString("fr-FR"))} · {pct} %</div>
                    </div>
                  )}
                  {a.cta?.type && a.cta.type !== "none" && (
                    <div className="text-[11px] mt-1.5 inline-flex items-center gap-1 chip">{t(`ads.ctalabel_${a.cta.label}`, CTA_LABELS[a.cta.label] || "Bouton")}</div>
                  )}
                  <div className="flex items-center gap-4 mt-3 text-xs flex-wrap" style={{ color: "var(--yalola-text-muted)" }}>
                    <span className="flex items-center gap-1"><Eye size={13} /> {a.impressions || 0}</span>
                    <span className="flex items-center gap-1"><MousePointerClick size={13} /> {a.clicks || 0}</span>
                    <span className="flex items-center gap-1"><TrendingUp size={13} /> {c}% CTR</span>
                    <span className="flex items-center gap-1"><Users size={13} /> {a.leads || 0}</span>
                    <span className="ml-auto">{a.budget} {curSym}</span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    {expired && a.renewal_discount?.percent && new Date(a.renewal_discount.expires_at) > new Date() ? (
                      <Button size="sm" className="pill pill-primary rounded-full flex-1" onClick={() => { setReactDays(a.days || 7); setReactivateAd(a); }} data-testid={`ad-reactivate-${a.id}`}>
                        <RefreshCw size={14} className="mr-1" /> {t("ads.reactivate", "Réactiver")} · 🎁 -{a.renewal_discount.percent} %
                      </Button>
                    ) : a.payment_status === "pending" ? (
                      <Button size="sm" className="pill pill-primary rounded-full flex-1" onClick={() => payNow(a)} data-testid={`ad-pay-${a.id}`}>
                        {t("ads.pay_credits", "Payer avec mes crédits")} ({fmtMoney(a.budget, a.currency || creditCur, lang)})
                      </Button>
                    ) : expired ? (
                      <Button size="sm" className="pill pill-primary rounded-full flex-1" onClick={() => { setReactDays(a.days || 7); setReactivateAd(a); }} data-testid={`ad-reactivate-${a.id}`}>
                        <RefreshCw size={14} className="mr-1" /> {t("ads.reactivate", "Réactiver")}
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-full flex-1" onClick={() => toggle(a)} data-testid={`ad-toggle-${a.id}`}>
                        {a.active ? t("ads.pause", "Mettre en pause") : t("ads.activate", "Activer")}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setStatsAd(a)} data-testid={`ad-stats-${a.id}`}><BarChart3 size={14} /></Button>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setLeadsAd(a)} data-testid={`ad-leads-${a.id}`}><Users size={14} /></Button>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => remove(a)} data-testid={`ad-delete-${a.id}`}><X size={14} /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>
      <Dialog open={!!reactivateAd} onOpenChange={(v) => !v && setReactivateAd(null)}>
        <DialogContent className="max-w-sm" data-testid="ad-reactivate-dialog">
          <DialogHeader>
            <DialogTitle>{t("ads.reactivate_title", "Réactiver la campagne")}</DialogTitle>
            <DialogDescription>{t("ads.reactivate_desc", "Choisissez la durée de prolongation. Le montant est débité de vos crédits publicitaires.")}</DialogDescription>
          </DialogHeader>
          {reactivateAd && (
            <div className="space-y-4">
              <div className="font-semibold truncate">{reactivateAd.title}</div>
              <div>
                <Label className="text-xs">{t("ads.reactivate_days", "Durée de prolongation")}</Label>
                <div className="flex gap-2 mt-1.5 flex-wrap">
                  {[reactivateAd.days || 7, 7, 14, 30].filter((v, i, arr) => arr.indexOf(v) === i).map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setReactDays(d)}
                      className="chip transition-colors"
                      style={reactDays === d ? { background: "var(--yalola-primary)", color: "#fff", borderColor: "transparent" } : {}}
                      data-testid={`ad-reactivate-days-${d}`}
                    >
                      {d} {t("ads.days_short", "j")}
                    </button>
                  ))}
                </div>
              </div>
              {(() => {
                const disc = reactivateAd.renewal_discount;
                const discActive = !!(disc?.percent && disc.expires_at && new Date(disc.expires_at) > new Date());
                const base = ((reactivateAd.base_budget || reactivateAd.budget || 0) / (reactivateAd.days || 7)) * reactDays;
                const amt = discActive ? base * (1 - disc.percent / 100) : base;
                return (
                  <>
                    {discActive && (
                      <div className="text-xs rounded-xl px-3 py-2 font-semibold" style={{ background: "#E7F6EC", color: "#1B7A3D" }} data-testid="ad-reactivate-discount">
                        🎁 {t("ads.renewal_discount", "Récompense performance")} : -{disc.percent} % {t("ads.discount_until", "jusqu'au")} {new Date(disc.expires_at).toLocaleDateString("fr-FR")}
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span style={{ color: "var(--yalola-text-muted)" }}>{t("ads.reactivate_price", "Montant")}</span>
                      <span className="font-bold text-lg" data-testid="ad-reactivate-amount">
                        {discActive && <span className="line-through mr-2 text-sm font-normal" style={{ color: "var(--yalola-text-muted)" }}>{base.toFixed(2)} {curSym}</span>}
                        {amt.toFixed(2)} {curSym}
                      </span>
                    </div>
                    {!isSuper && <AdCreditBalance balance={creditBalance} currency={creditCur} need={Math.max(1, amt)} loading={!credits} onBeforeNavigate={() => setReactivateAd(null)} />}
                    <Button className="w-full pill-primary rounded-full" onClick={reactivate} disabled={!isSuper && Math.max(1, amt) > creditBalance} data-testid="ad-reactivate-pay">
                      {t("ads.reactivate_pay", "Réactiver avec mes crédits")}
                    </Button>
                  </>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <LeadsDialog ad={leadsAd} open={!!leadsAd} onOpenChange={(v) => !v && setLeadsAd(null)} />
      <StatsDialog ad={statsAd} open={!!statsAd} onOpenChange={(v) => !v && setStatsAd(null)} />
    </AppShell>
  );
};

export default AdsManager;
